/**
 * EconomyEngine — all money & demand math for a single simulated day.
 *
 * DESIGN NOTES
 * ------------
 * • Two ledgers are maintained, like a real business:
 *    – Cash: inventory is PAID for when POs are placed; refunds, fees,
 *      payroll and ad spend hit cash immediately; wholesale revenue arrives
 *      net-60 via receivables.
 *    – P&L (quarter accumulator): COGS recognized at sale using each SKU's
 *      landed-cost moving average. This gap is the classic DTC cash-flow
 *      trap the game teaches.
 * • Demand models are calibrated so an early-stage brand does ~2–4
 *   queen-equivalents/day (~$1–2k revenue) and a late-game empire does
 *   hundreds — compressed vs reality for playability, ratios preserved.
 * • All multipliers come from state modifiers (events/research/team) via
 *   `getMods()`; no hidden magic constants outside constants.ts tuning.
 */

import {
  AMAZON_FBA_FULFILLMENT_PER_UNIT,
  DIFFICULTIES,
  HANDLING_PER_UNIT_INBOUND,
  HOLDING_COST_PER_UNIT_PER_DAY,
  INBOUND_FREIGHT_PER_UNIT,
  MARKETING_CHANNELS,
  RETURNS_REVERSE_LOGISTICS,
  SHOPIFY_SHIP_PER_UNIT_BASE,
  SHOWROOM_DEMAND_BONUS,
  SHOWROOM_DEMAND_CAP,
  SUPPLIERS,
  TUNING,
} from './constants';
import { clamp } from './format';
import { blendedAsp, priceScore, reviewScore } from './products';
import { has } from './research';
import type {
  GameState,
  MarketingChannelId,
  Modifier,
  ModifierField,
  Product,
} from './types';
import { adAuctionHeat, seasonalMultiplier } from './world';

// ---------------------------------------------------------------------------
// Modifier aggregation
// ---------------------------------------------------------------------------

export interface ModAggregate {
  mult: number;
  add: number;
}

export function getMods(s: GameState, field: ModifierField): ModAggregate {
  let mult = 1;
  let add = 0;
  const today = s.time.day;
  for (const m of s.modifiers) {
    if (m.field !== field || m.expiresDay < today) continue;
    if (m.mult !== undefined) mult *= m.mult;
    if (m.add !== undefined) add += m.add;
  }
  return { mult, add };
}

export function activeModifiers(s: GameState): Modifier[] {
  return s.modifiers.filter((m) => m.expiresDay >= s.time.day);
}

// ---------------------------------------------------------------------------
// Cost curves
// ---------------------------------------------------------------------------

/** Fuel-adjusted FBA-style fulfillment per unit. */
export function fulfillmentPerUnit(s: GameState): number {
  const fm = freightMultiplier(s);
  return AMAZON_FBA_FULFILLMENT_PER_UNIT * (0.88 + 0.12 * fm);
}

/** Freight multiplier from lagged oil + compression research + events. */
export function freightMultiplier(s: GameState): number {
  const oilPart = 0.5 + s.world.oilLagged / 160;
  let research = 1;
  if (has(s, 'compression-2')) research *= 0.96;
  const { mult } = getMods(s, 'freightMult');
  return oilPart * research * mult;
}

/** Outbound parcel cost per unit (Shopify channel, fuel-linked). */
export function outboundParcelPerUnit(s: GameState): number {
  const fm = freightMultiplier(s);
  return SHOPIFY_SHIP_PER_UNIT_BASE * Math.pow(fm, 0.7);
}

/**
 * Fully-landed unit cost estimate for a product from a given supplier today.
 * Used both for PO quotes and for updating the landed-cost moving average.
 */
export function landedUnitCost(
  s: GameState,
  p: Product,
  supplierId: string,
): number {
  const def = SUPPLIERS.find((d) => d.id === supplierId);
  if (!def) throw new Error(`Unknown supplier: ${supplierId}`);
  const tariff = s.tariffs[def.country] ?? 1;
  const foam = s.world.foamIndex / 100 + getMods(s, 'foamAdd').add / 100;
  let cogsMult = getMods(s, 'cogsMult').mult;
  if (has(s, 'in-house-machinery')) cogsMult *= 0.91;
  const factory = p.baseFactoryCost * def.costMultiplier * foam;
  const freight =
    INBOUND_FREIGHT_PER_UNIT[def.country] * freightMultiplier(s) *
    (has(s, 'compression-2') ? 0.92 : 1);
  const handling = HANDLING_PER_UNIT_INBOUND;
  // Vertical integration: structural COGS discount + commodity shocks halved.
  if (has(s, 'vertical-foam')) {
    cogsMult *= 0.9;
    const shock = Math.max(0, foam - 1.05);
    cogsMult -= shock * 0.5;
  }
  return factory * tariff * cogsMult + freight + handling;
}

// ---------------------------------------------------------------------------
// Per-SKU economics
// ---------------------------------------------------------------------------

/** Effective return rate for a SKU today (event modifiers can spike this). */
export function returnRateFor(s: GameState, p: Product): number {
  let rr = p.returnRateBase - ((p.quality - 50) / 100) * 0.06;
  if (has(s, 'service-playbook')) rr -= 0.015;
  if (s.team.hires.find((h) => h.id === 'cs-agent')?.hired) rr -= 0.012;
  if (has(s, 'forecasting')) rr -= 0.002;
  rr += getMods(s, 'returnRateAdd').add;
  return clamp(rr, 0.04, 0.28);
}

/** Blended Shopify conversion rate (sessions → orders). */
export function shopifyConversion(s: GameState, avgPriceScore: number): number {
  let cvr = s.channels.shopify.conversionRateBase;
  cvr += (avgQuality(s) / 100) * 0.010;
  cvr += avgRating(s) >= 4.3 ? 0.004 : 0;
  cvr += priceTrustBonus(avgPriceScore);
  if (has(s, 'certipur')) cvr += 0.006;
  if (has(s, 'reshore-pilot')) cvr += 0.003;
  cvr *= getMods(s, 'conversionMult').mult;
  return clamp(cvr, 0.004, 0.05);
}

function priceTrustBonus(priceScoreAvg: number): number {
  // Aggressive pricing converts better; premium pricing needs brand heat.
  return clamp((priceScoreAvg - 1) * 0.008, -0.006, 0.006);
}

export function avgQuality(s: GameState): number {
  const act = activeProducts(s);
  if (!act.length) return 55;
  return act.reduce((a, p) => a + p.quality, 0) / act.length;
}

export function avgRating(s: GameState): number {
  const act = activeProducts(s);
  if (!act.length) return 4.0;
  const weighted = act.reduce((a, p) => a + p.reviews.rating * Math.max(p.reviews.count, 1), 0);
  const wsum = act.reduce((a, p) => a + Math.max(p.reviews.count, 1), 0);
  return weighted / wsum;
}

export function activeProducts(s: GameState): Product[] {
  return s.products.filter((p) => p.status === 'active');
}

/**
 * Demand multiplier from physical showrooms (retail halo). Each location
 * contributes by tier, capped — foot traffic, local brand trust, and the
 * "try before you buy" effect lifted every channel in real DTC retail.
 */
export function showroomDemandFactor(s: GameState): number {
  if (!s.showrooms || s.showrooms.length === 0) return 1;
  const raw = s.showrooms.reduce((a, r) => a + SHOWROOM_DEMAND_BONUS[r.tier], 0);
  return 1 + Math.min(SHOWROOM_DEMAND_CAP, raw);
}

// ---------------------------------------------------------------------------
// Marketing engine
// ---------------------------------------------------------------------------

export interface ChannelDayStats {
  spend: number;
  orders: number;
  effectiveCac: number;
}

export interface MarketingResult {
  totalSpend: number;
  perChannel: Record<MarketingChannelId, ChannelDayStats>;
  shopifyPaidOrders: number;
  amazonAdsBoost: number;
  awarenessGain: number;
  emailOrders: number;
  viralTriggered: boolean;
}

export function cacEffective(s: GameState, chId: MarketingChannelId): number {
  const def = MARKETING_CHANNELS.find((c) => c.id === chId)!;
  const diff = DIFFICULTIES[s.meta.difficulty];
  let cac = def.baseCac;
  cac *= diff.cacMult;
  cac *= clamp(0.82 + 0.33 * s.world.competition, 0.75, 1.75);
  cac *= clamp(1.1 - s.player.awareness / 700, 0.72, 1.1);
  const fatigue = s.marketing.fatigue[chId];
  cac *= 1 + fatigue * 0.55;
  cac *= adAuctionHeat(s.time.day);
  if (has(s, 'brand-refresh')) cac *= 0.9;
  if (s.team.hires.find((h) => h.id === 'growth-analyst')?.hired) cac *= 0.92;
  cac *= getMods(s, 'cacMult').mult;
  return Math.max(12, cac);
}

export function runMarketing(s: GameState, rng: () => number): MarketingResult {
  const res: MarketingResult = {
    totalSpend: 0,
    perChannel: {} as Record<MarketingChannelId, ChannelDayStats>,
    shopifyPaidOrders: 0,
    amazonAdsBoost: 0,
    awarenessGain: 0,
    emailOrders: 0,
    viralTriggered: false,
  };
  if (!s.marketing.enabled) {
    // Fatigue fully recovers when ads are off.
    for (const c of MARKETING_CHANNELS) s.marketing.fatigue[c.id] = Math.max(0, s.marketing.fatigue[c.id] - c.recoveryPerDay * 2);
    return res;
  }

  const analystSlow = s.team.hires.find((h) => h.id === 'growth-analyst')?.hired ? 0.75 : 1;
  const refreshSlow = has(s, 'brand-refresh') ? 0.8 : 1;

  for (const def of MARKETING_CHANNELS) {
    const alloc = clamp(s.marketing.allocation[def.id], 0, 1);
    const raw = s.marketing.dailyBudget * alloc;
    const stats: ChannelDayStats = { spend: raw, orders: 0, effectiveCac: 0 };

    if (raw > 0 && alloc > 0.001) {
      // Saturation: tanh response above the channel's comfortable scale.
      const eff = def.saturation * Math.tanh(raw / def.saturation);
      const cac = cacEffective(s, def.id);
      const orders = eff / cac;
      stats.effectiveCac = raw / orders;
      stats.orders = orders;

      // Fatigue grows with sustained pressure.
      s.marketing.fatigue[def.id] = clamp(
        s.marketing.fatigue[def.id] + def.fatiguePerDay * analystSlow * refreshSlow * (raw / def.saturation),
        0,
        1,
      );

      // Brand-building spillover (diminishing near ceiling).
      const headroom = clamp(1 - s.player.awareness / 110, 0, 1);
      res.awarenessGain += (def.brandBoost * raw) / 100 * headroom;

      if (def.id === 'tiktok' && raw >= TUNING.tiktokViralMinSpend) {
        const p = TUNING.tiktokViralChancePerDay * Math.sqrt(raw / TUNING.tiktokViralMinSpend);
        if (rng() < p) res.viralTriggered = true;
      }
      res.totalSpend += raw;
    } else {
      // Recovery when dark.
      s.marketing.fatigue[def.id] = Math.max(0, s.marketing.fatigue[def.id] - def.recoveryPerDay);
    }

    res.perChannel[def.id] = stats;
  }

  res.shopifyPaidOrders =
    res.perChannel.meta.orders +
    res.perChannel.google.orders * 0.55 + // some search demand leaks to Amazon
    res.perChannel.tiktok.orders +
    res.perChannel.influencer.orders;
  res.amazonAdsBoost =
    (res.perChannel.amazonAds.spend / 320) * // ad dollars feed rank flywheel
    (1 + 0.4 * Math.tanh(res.perChannel.amazonAds.spend / 600));
  res.emailOrders = Math.min(
    res.perChannel.email.orders,
    s.marketing.emailListSize * 0.004,
  );
  res.totalSpend = Object.values(res.perChannel).reduce((a, c) => a + c.spend, 0);
  return res;
}

// ---------------------------------------------------------------------------
// Sales simulation (one day)
// ---------------------------------------------------------------------------

export interface DayResult {
  revenue: number;
  units: number;
  returnsUnits: number;
  refundLoss: number;
  returnsCost: number;
  amazonRevenue: number;
  shopifyRevenue: number;
  wholesaleRevenueBooked: number;
  amazonUnits: number;
  shopifyUnits: number;
  wholesaleUnits: number;
  amazonFees: number;
  shopifyFees: number;
  cogsExpense: number;
  holdingCost: number;
  stockouts: boolean;
  newCustomers: number;
}

export function runDailySales(
  s: GameState,
  rng: () => number,
  mk: MarketingResult,
): DayResult {
  const out: DayResult = {
    revenue: 0, units: 0, returnsUnits: 0, refundLoss: 0, returnsCost: 0,
    amazonRevenue: 0, shopifyRevenue: 0, wholesaleRevenueBooked: 0,
    amazonUnits: 0, shopifyUnits: 0, wholesaleUnits: 0,
    amazonFees: 0, shopifyFees: 0, cogsExpense: 0, holdingCost: 0,
    stockouts: false, newCustomers: 0,
  };
  const act = activeProducts(s);
  if (act.length === 0) return out;

  const season = seasonalMultiplier(s.time.day);
  const diff = DIFFICULTIES[s.meta.difficulty];
  const demandEnv =
    diff.demandMult *
    season *
    showroomDemandFactor(s) *
    getMods(s, 'demandMult').mult *
    clamp(1.25 - 0.28 * (s.world.competition - 1), 0.55, 1.3);

  // --- Amazon demand -------------------------------------------------------
  let amazonUnitsWanted = 0;
  if (s.channels.amazon.enabled && !s.channels.amazon.suspended) {
    const reviewMult = clamp(0.45 + (avgRating(s) - 3.0) * 0.42, 0.3, 1.5);
    const rankTerm = Math.pow(Math.max(s.channels.amazon.organicRankScore, 0.5), 0.95);
    const priceAvg = act.reduce((a, p) => a + priceScore(s, p), 0) / act.length;
    amazonUnitsWanted =
      0.5 *
      rankTerm *
      reviewMult *
      clamp(priceAvg, 0.3, 1.5) *
      demandEnv *
      (1 + mk.amazonAdsBoost * 0.35) *
      getMods(s, 'amazonTrafficMult').mult *
      (0.82 + rng() * 0.36);
  }

  // --- Shopify demand ------------------------------------------------------
  const priceAvgShop = act.reduce((a, p) => a + priceScore(s, p), 0) / act.length;
  const cvr = shopifyConversion(s, priceAvgShop);
  const organicSessions =
    TUNING.shopifyOrganicPerAwareness *
    Math.pow(s.player.awareness, 1.15) *
    (has(s, 'certipur') ? 1.1 : 1) *
    getMods(s, 'shopifyTrafficMult').mult;
  const returningOrders = s.marketing.returningCustomerPool * TUNING.returningShare;
  const shopifyOrdersWanted = mk.shopifyPaidOrders + organicSessions * cvr + returningOrders + mk.emailOrders;

  // Allocate wanted demand across SKUs by attractiveness & availability.
  const weights = act.map((p) => {
    const inv = s.inventory[p.id] ?? 0;
    const available = inv > 0 ? 1 : 0;
    return available * priceScore(s, p) * reviewScore(p) * (p.archetype === 'luxury' ? 0.7 : 1);
  });
  const weightSum = weights.reduce((a, b) => a + b, 0);

  const allocate = (wanted: number): Map<string, number> => {
    const map = new Map<string, number>();
    if (weightSum <= 0 || wanted <= 0) return map;
    let remaining = wanted;
    act.forEach((p, i) => {
      const inv = s.inventory[p.id] ?? 0;
      const desired = (wanted * weights[i]) / weightSum;
      const got = Math.min(desired, inv, remaining);
      if (got > 0) map.set(p.id, got);
      remaining -= got;
    });
    if (remaining > 0.5) out.stockouts = true;
    return map;
  };

  const amazonAlloc = allocate(amazonUnitsWanted);
  const shopifyAlloc = allocate(shopifyOrdersWanted);

  const sellThrough = (
    alloc: Map<string, number>,
    channel: 'amazon' | 'shopify',
  ) => {
    for (const [pid, unitsFrac] of alloc) {
      const units = Math.floor(unitsFrac) + (rng() < unitsFrac % 1 ? 1 : 0);
      if (units <= 0) continue;
      const prod = s.products.find((x) => x.id === pid)!;
      sellUnits(s, out, prod, units, channel);
    }
  };
  sellThrough(amazonAlloc, 'amazon');
  sellThrough(shopifyAlloc, 'shopify');

  // --- Fees -----------------------------------------------------------------
  const amz = s.channels.amazon;
  const feePct = amz.feePercent * getMods(s, 'amazonFeeMult').mult;
  out.amazonFees +=
    out.amazonRevenue * feePct + out.amazonUnits * fulfillmentPerUnit(s);
  out.shopifyFees +=
    out.shopifyRevenue * s.channels.shopify.paymentFeePercent +
    out.shopifyUnits * outboundParcelPerUnit(s);

  // --- Returns processing ---------------------------------------------------
  processReturns(s, out, rng);

  // --- Holding costs ---------------------------------------------------------
  let heldUnits = 0;
  for (const pid of Object.keys(s.inventory)) heldUnits += s.inventory[pid] ?? 0;
  out.holdingCost =
    heldUnits *
    HOLDING_COST_PER_UNIT_PER_DAY *
    (has(s, 'forecasting') ? 0.75 : 1);

  // --- Customer & list growth ------------------------------------------------
  out.newCustomers = out.amazonUnits + out.shopifyUnits + out.wholesaleUnits;
  s.marketing.emailListSize += Math.round(out.newCustomers * 0.35);
  s.marketing.returningCustomerPool += out.newCustomers * 0.62;

  return out;
}

/** Sell `units` of a product through a channel; updates inventory & P&L fields. */
function sellUnits(
  s: GameState,
  out: DayResult,
  p: Product,
  unitsIn: number,
  channel: 'amazon' | 'shopify',
): void {
  // Never sell more than physically on hand (channel weights can go stale
  // within a single day when both channels draw from the same pool).
  const available = s.inventory[p.id] ?? 0;
  const units = Math.min(unitsIn, Math.max(0, Math.floor(available)));
  if (units <= 0) return;

  const asp = blendedAsp(p);
  const revenue = units * asp;

  s.inventory[p.id] = available - units;
  p.unitsSoldTotal += units;
  out.units += units;
  out.revenue += revenue;
  // COGS recognized at the SKU's landed-cost moving average — cost shocks
  // enter through new POs (paid cash), exactly like real inventory accounting.
  out.cogsExpense += units * p.landedCost;

  if (channel === 'amazon') {
    out.amazonRevenue += revenue;
    out.amazonUnits += units;
  } else {
    out.shopifyRevenue += revenue;
    out.shopifyUnits += units;
  }
}

function processReturns(s: GameState, out: DayResult, rng: () => number): void {
  if (out.units <= 0) return;
  // Blended return rate weighted by review volume (proxy for sales mix).
  const act = activeProducts(s);
  const blended = act.length
    ? act.reduce((a, p) => a + returnRateFor(s, p) * Math.max(1, p.reviews.count), 0) /
      act.reduce((a, p) => a + Math.max(1, p.reviews.count), 0)
    : 0.12;
  const expected = out.units * blended;
  const returns = Math.round(expected + (rng() - 0.5) * Math.sqrt(expected + 1));
  if (returns <= 0) return;
  out.returnsUnits = returns;
  // Full-refund trial policy (industry standard for bed-in-a-box).
  const blendedAspToday = out.revenue / Math.max(1, out.units);
  out.refundLoss = returns * blendedAspToday;
  out.returnsCost = returns * RETURNS_REVERSE_LOGISTICS;
}

// ---------------------------------------------------------------------------
// Composite health metrics (dashboard "Sleep Score")
// ---------------------------------------------------------------------------

export function sleepScore(s: GameState): number {
  const q = avgQuality(s);
  const rr = clamp(1 - blendedReturnRate(s) / 0.16, 0, 1);
  const rating = clamp((avgRating(s) - 3) / 2, 0, 1);
  const rep = s.player.reputation / 100;
  return Math.round((0.38 * q + 0.24 * rr * 100 + 0.23 * rating * 100 + 0.15 * rep * 100));
}

export function blendedReturnRate(s: GameState): number {
  const act = activeProducts(s);
  if (!act.length) return 0.12;
  return act.reduce((a, p) => a + returnRateFor(s, p), 0) / act.length;
}
